import { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { settingsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { User, Lock, Eye, EyeOff, Check } from 'lucide-react';

const Section = ({ icon: Icon, title, subtitle, children }) => (
  <motion.div
    className="glass rounded-2xl p-6"
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
  >
    <div className="flex items-center gap-3 mb-6 pb-5 border-b border-white/5">
      <div className="w-10 h-10 rounded-xl bg-violet-500/15 flex items-center justify-center">
        <Icon className="w-5 h-5 text-violet-400" />
      </div>
      <div>
        <h2 className="text-white font-semibold">{title}</h2>
        {subtitle && <p className="text-slate-500 text-xs mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {children}
  </motion.div>
);

export default function SettingsPage() {
  const { user, updateUser } = useAuth();

  // Profile state
  const [name, setName] = useState(user?.name || '');
  const [nameSaving, setNameSaving] = useState(false);

  // Password state
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);

  const handleSaveName = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setNameSaving(true);
    try {
      await settingsAPI.updateName(name.trim());
      updateUser({ name: name.trim() });
      toast.success('Display name updated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update name');
    } finally {
      setNameSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPwd !== confirmPwd) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPwd.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setPwdSaving(true);
    try {
      await settingsAPI.changePassword(currentPwd, newPwd);
      toast.success('Password changed successfully!');
      setCurrentPwd('');
      setNewPwd('');
      setConfirmPwd('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwdSaving(false);
    }
  };

  return (
    <div data-testid="settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Account Settings</h1>
        <p className="text-slate-400">Manage your account details and security</p>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Account Info */}
        <Section icon={User} title="Account Info" subtitle="Update your display name">
          {/* Avatar preview */}
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-2xl font-bold text-white">
              {(name || user?.username || 'U').charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="text-white font-medium">{name || user?.name || 'Your Name'}</p>
              <p className="text-slate-500 text-sm">{user?.username}</p>
            </div>
          </div>

          <form onSubmit={handleSaveName} className="space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Display Name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                className="input-field"
                required
                data-testid="settings-name-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">Email / Username</label>
              <Input
                value={user?.username || ''}
                readOnly
                className="input-field opacity-50 cursor-not-allowed"
                data-testid="settings-email-display"
              />
              <p className="text-slate-600 text-xs mt-1">Email cannot be changed</p>
            </div>
            <Button
              type="submit"
              className="btn-primary flex items-center gap-2"
              disabled={nameSaving || !name.trim()}
              data-testid="settings-save-name-btn"
            >
              {nameSaving ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              {nameSaving ? 'Saving…' : 'Save Changes'}
            </Button>
          </form>
        </Section>

        {/* Security */}
        <Section icon={Lock} title="Security" subtitle="Change your account password">
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Current Password</label>
              <div className="relative">
                <Input
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPwd}
                  onChange={(e) => setCurrentPwd(e.target.value)}
                  placeholder="Enter current password"
                  className="input-field pr-10"
                  required
                  data-testid="settings-current-pwd"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm text-slate-400 mb-2">New Password</label>
              <div className="relative">
                <Input
                  type={showNew ? 'text' : 'password'}
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  placeholder="Min 6 characters"
                  className="input-field pr-10"
                  required
                  data-testid="settings-new-pwd"
                />
                <button
                  type="button"
                  onClick={() => setShowNew(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm text-slate-400 mb-2">Confirm New Password</label>
              <Input
                type="password"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                placeholder="Repeat new password"
                className={`input-field ${confirmPwd && confirmPwd !== newPwd ? 'border-red-500/50' : ''}`}
                required
                data-testid="settings-confirm-pwd"
              />
              {confirmPwd && confirmPwd !== newPwd && (
                <p className="text-red-400 text-xs mt-1">Passwords do not match</p>
              )}
            </div>

            <Button
              type="submit"
              className="btn-primary flex items-center gap-2"
              disabled={pwdSaving || !currentPwd || !newPwd || !confirmPwd}
              data-testid="settings-change-pwd-btn"
            >
              {pwdSaving ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Lock className="w-4 h-4" />
              )}
              {pwdSaving ? 'Updating…' : 'Update Password'}
            </Button>
          </form>
        </Section>
      </div>
    </div>
  );
}
